import { Link, useParams } from "react-router-dom";
import { CATEGORIES, getCategory, projectsIn, projectPath } from "@/data/site";
import { PageHero } from "@/components/PageHero";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { ArrowLink } from "@/components/ArrowLink";
import { Seo } from "@/components/Seo";
import NotFound from "@/pages/NotFound";

const ProjectRow = ({ p, i }) => (
  <article className="grid items-center gap-10 border-t border-heritage/15 py-14 lg:grid-cols-12 lg:py-12" data-testid={`project-row-${p.slug}`}>
    <FadeUp className={`lg:col-span-6 ${i % 2 ? "lg:order-2 lg:col-start-7" : ""}`}>
      <Link to={projectPath(p)} className="group block overflow-hidden" data-testid={`project-row-image-link-${p.slug}`}>
        <Photo
          image={p.image}
          alt={p.name}
          className={`aspect-[4/3] lg:aspect-[16/10] ${i % 2 ? "rounded-tl-[160px]" : "rounded-tr-[160px]"}`}
          imgClassName="transition-transform duration-[1.6s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.05]"
        />
      </Link>
    </FadeUp>
    <FadeUp delay={0.1} className={`lg:col-span-5 ${i % 2 ? "lg:order-1" : "lg:col-start-8"}`}>
      <p className="font-caps text-sm text-gold-deep">{String(i + 1).padStart(2, "0")}</p>
      <h2 className="mt-3 font-display text-4xl leading-tight text-heritage md:text-5xl">{p.name}</h2>
      <div className="mt-5"><StatusBadge status={p.status} testid={`status-badge-${p.slug}`} /></div>
      <p className="mt-6 text-lg leading-relaxed text-muted-ink">{p.summary}</p>
      <div className="mt-8"><ArrowLink to={projectPath(p)} testid={`view-project-${p.slug}`}>View project</ArrowLink></div>
    </FadeUp>
  </article>
);

export default function Category() {
  const { category } = useParams();
  const cat = getCategory(category);
  if (!cat) return <NotFound />;
  const projects = projectsIn(cat.slug);
  const others = CATEGORIES.filter((c) => c.slug !== cat.slug);

  return (
    <>
      <Seo
        title={cat.title}
        path={`/${cat.slug}`}
        description={`${cat.title} at Little Englishman's Bay Estate, Tobago: ${projects.map((p) => p.name).join(", ")}.`}
      />
      <PageHero eyebrow={`${cat.numeral} · ${cat.tagline}`} lines={cat.title.split(" & ").map((s, i, a) => (i < a.length - 1 ? `${s} &` : s))} image={cat.heroImage ?? cat.image} alt={cat.title} testid="category-hero" />
      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 lg:py-20">
        <FadeUp className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-4"><Eyebrow>{projects.length} {projects.length === 1 ? "Project" : "Projects"}</Eyebrow></div>
          <p className="font-display text-3xl leading-snug text-heritage md:text-4xl lg:col-span-8" data-testid="category-intro">{cat.intro}</p>
        </FadeUp>
        <div className="mt-14">
          {projects.map((p, i) => <ProjectRow key={p.slug} p={p} i={i} />)}
        </div>
      </section>
      <section className="border-t border-gold/30 bg-ivory-muted py-16" data-testid="other-categories">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <Eyebrow>Other areas of the estate</Eyebrow>
          <div className="mt-8 grid gap-px bg-heritage/10 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((c) => (
              <Link key={c.slug} to={`/${c.slug}`} className="group bg-ivory-muted p-6 transition-colors duration-500 hover:bg-heritage" data-testid={`other-cat-${c.slug}`}>
                <span className="font-caps text-sm text-gold-deep group-hover:text-gold">{c.numeral}</span>
                <span className="mt-2 block font-display text-2xl text-heritage group-hover:text-ivory">{c.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
